import { KeyboardEvent } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { anchorText } from 'lib/common';
import { newlinesToLineBreak } from 'lib/helpers';
import {
  CalendarEventDateIcon,
  CalendarEventDateString,
  CalendarEventQuickNavigation,
  CalendarEventStatus
} from 'components/calendar';
import { ContentValue } from 'components/common';

import type { CalendarEventExtended } from 'shared-types';

interface Props {
  event: CalendarEventExtended;
  onEventClick: (event: CalendarEventExtended) => void;
}

const CalendarEventCalendarListItem = ({ event, onEventClick }: Props) => {
  const { theme } = useReduxState();

  const handleTitleKeyDown = (keyDownEvent: KeyboardEvent<HTMLDivElement>) => {
    if (keyDownEvent.key === 'Enter' || keyDownEvent.code === 'Space') {
      keyDownEvent.preventDefault();
      onEventClick(event);
    }
  };

  const contentValueStyle = {
    color: theme.textColorLighter,
    fontSize: '1.5rem',
    fontWeight: 600
  };

  const contentValueIconStyle = {
    fontSize: '1.3rem',
    marginRight: 6,
    top: 1
  };

  return (
    <div className="item">
      <div className="content">
        <div className="date-icon">
          <CalendarEventDateIcon startDate={event.startDate} />
        </div>

        <div className="info">
          <div
            className="title"
            onClick={() => onEventClick(event)}
            onKeyDown={handleTitleKeyDown}
            role="button"
            tabIndex={0}
          >
            {event.title}
          </div>
          <div className="subtitle">
            <div className="time">
              <ContentValue
                icon={['far', 'calendar-alt']}
                iconStyle={contentValueIconStyle}
                style={contentValueStyle}
              >
                <CalendarEventDateString
                  startDate={event.startDate}
                  endDate={event.endDate}
                />
              </ContentValue>
            </div>
            {event.location ? (
              <div className="location">
                <ContentValue
                  icon="map-marker-alt"
                  iconStyle={contentValueIconStyle}
                  style={contentValueStyle}
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: newlinesToLineBreak(
                        anchorText({ text: event.location, sanitize: true })
                      )
                    }}
                  />
                </ContentValue>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="status">
        <CalendarEventStatus event={event} />
      </div>

      <div className="actions">
        <CalendarEventQuickNavigation event={event} size="header" />
      </div>

      <style jsx>{`
        .item {
          align-items: flex-start;
          border: 1px solid ${theme.lightGreyDark};
          border-radius: 7px;
          display: flex;
          flex-direction: column;
          gap: 15px 25px;
          padding: 15px 15px;
        }
        .content {
          display: flex;
        }
        .date-icon {
          margin-right: 15px;
          margin-top: 3px;
        }
        .title {
          color: ${theme.textColor};
          cursor: pointer;
          font-size: 1.6rem;
          font-weight: 700;
        }
        .title:hover {
          text-decoration: underline;
        }
        .subtitle {
          color: ${theme.textColorLighter};
          display: flex;
          flex-wrap: wrap;
          font-size: 1.3rem;
          gap: 5px 40px;
          margin-top: 6px;
        }
        .status,
        .actions {
          flex-shrink: 0;
          margin-left: 45px;
        }
        .actions {
          display: flex;
          justify-content: flex-end;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .item {
            align-items: center;
            flex-direction: row;
          }
          .content {
            width: 600px;
          }
          .status,
          .actions {
            margin-left: 0;
          }
          .actions {
            flex-grow: 1;
            min-width: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default CalendarEventCalendarListItem;
